export type ConsentCategories = {
  analytics: boolean;
  marketing: boolean;
};

export type CookieConsentValue = ConsentCategories & {
  necessary: true;
  version: 1;
  decidedAt: string;
};

export const CONSENT_COOKIE = "cv-cookie-consent";
export const CONSENT_EVENT = "cv:consent-change";
export const CONSENT_OPEN_SETTINGS_EVENT = "cv:consent-open-settings";
const CONSENT_MAX_AGE_SECONDS = 60 * 60 * 24 * 180;

export function readConsent(): CookieConsentValue | null {
  if (typeof document === "undefined") return null;
  const raw = document.cookie
    .split("; ")
    .find((entry) => entry.startsWith(`${CONSENT_COOKIE}=`))
    ?.slice(CONSENT_COOKIE.length + 1);
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(decodeURIComponent(raw));
    if (
      typeof parsed !== "object" ||
      parsed === null ||
      typeof (parsed as CookieConsentValue).analytics !== "boolean" ||
      typeof (parsed as CookieConsentValue).marketing !== "boolean"
    ) {
      return null;
    }
    return { ...(parsed as CookieConsentValue), necessary: true };
  } catch {
    return null;
  }
}

function serializeConsent(value: ConsentCategories): string {
  const payload: CookieConsentValue = {
    necessary: true,
    version: 1,
    decidedAt: new Date().toISOString(),
    ...value,
  };
  return encodeURIComponent(JSON.stringify(payload));
}

/** Persists the decision and notifies subscribers (trackers, banner, modal). */
export function writeConsent(value: ConsentCategories) {
  if (typeof document === "undefined") return;
  document.cookie = `${CONSENT_COOKIE}=${serializeConsent(value)}; path=/; max-age=${CONSENT_MAX_AGE_SECONDS}; samesite=lax`;
  window.dispatchEvent(new CustomEvent(CONSENT_EVENT));
}

export function subscribeToConsent(listener: () => void) {
  window.addEventListener(CONSENT_EVENT, listener);
  window.addEventListener(CONSENT_OPEN_SETTINGS_EVENT, listener);
  return () => {
    window.removeEventListener(CONSENT_EVENT, listener);
    window.removeEventListener(CONSENT_OPEN_SETTINGS_EVENT, listener);
  };
}

export function openConsentSettings() {
  window.dispatchEvent(new CustomEvent(CONSENT_OPEN_SETTINGS_EVENT));
}
